import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TopBar } from '@/components/shell/TopBar';
import { BottomNav } from '@/components/shell/BottomNav';
import { MiniPlayer } from '@/components/shell/MiniPlayer';
import { EmailVerificationBanner } from '@/components/shell/EmailVerificationBanner';
import { ONBOARDING_BYPASS_PATHS } from '@/components/auth/OnboardingGuard';
import { WorldSelectionSheet } from '@/components/worlds/WorldSelectionSheet';
import { WorldSelectionSheetProvider } from '@/components/worlds/WorldSelectionSheetContext';
import { useAuthStore } from '@/stores/authStore';
import { useAudioStore } from '@/stores/audioStore';
import { useUiStore } from '@/stores/uiStore';
import { useMediaSession } from '@/components/shell/useMediaSession';

/**
 * World-route bg overrides — when the active route matches a per-world
 * Builder or Story-Output URL, the AppShell outer wrapper paints the
 * world's surface colour instead of the cream `bg-dream-background`
 * default. Closes the cream-gap-below-route bug fully (PWA #16 Sub-Phase
 * 16.2 bug-fix 2026-04-29): main's pb area is now inside the AppShell
 * outer wrapper that's already painting sky-blue / navy / cream, so
 * there's no layer to peek through. Pattern carries to Story-Output in
 * Phase 4/5 — regex includes `/story/...` ahead of those routes
 * shipping. Static literal map (not template-literal) so Tailwind JIT
 * picks up all three world classes.
 */
const WORLD_ROUTE_BG: Record<string, string> = {
  aa: 'bg-world-aa',
  kp: 'bg-world-kp',
  sb: 'bg-world-sb',
};
const WORLD_ROUTE_PATTERN = /^\/(?:builder|story)\/(aa|kp|sb)\b/;

export function AppShell() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();

  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const fetchCurrentUser = useAuthStore((s) => s.fetchCurrentUser);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  // Singleton <audio> element + audioStore binding. The store dispatches
  // commands via a non-reactive `_audio` ref; this component owns the DOM
  // node and wires playback lifecycle events back into the store.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const bindAudio = useAudioStore((s) => s.bindAudio);
  const onTimeUpdate = useAudioStore((s) => s._onTimeUpdate);
  const onPlay = useAudioStore((s) => s._onPlay);
  const onPause = useAudioStore((s) => s._onPause);
  const onEnded = useAudioStore((s) => s._onEnded);
  const onAudioError = useAudioStore((s) => s._onError);

  // Suppress the MiniPlayer while the in-place catalog reader is open (it hosts
  // its own player). Subscribed here with the other hooks — before any early
  // return — so the hook order stays stable.
  const catalogReaderOpen = useUiStore((s) => s.catalogReaderOpen);
  // Paket 3 — same double-render suppression while the whole-world
  // Gesamt-Hörbuch CombinedAudioPlayer is mounted on /catalog/:slug.
  // State-driven (not route-driven): flips only while the player is on
  // screen, so a user browsing Wissenswelten with an unrelated story
  // playing keeps their MiniPlayer controls until the combined player
  // itself surfaces.
  const catalogWorldCombinedPlayerOpen = useUiStore(
    (s) => s.catalogWorldCombinedPlayerOpen,
  );

  const needsHydration = accessToken !== null && user === null;
  const [hydrating, setHydrating] = useState(needsHydration);
  const hydrationStarted = useRef(false);

  useEffect(() => {
    if (hydrationStarted.current || !needsHydration) return;
    hydrationStarted.current = true;

    fetchCurrentUser()
      .catch(() => {
        clearAuth();
      })
      .finally(() => {
        setHydrating(false);
      });
  }, [needsHydration, fetchCurrentUser, clearAuth]);

  useEffect(() => {
    bindAudio(audioRef.current);
    return () => bindAudio(null);
  }, [bindAudio]);

  // Wire Media Session API (lock-screen metadata + action handlers).
  useMediaSession();

  // Fire `abandoned` beacon on pagehide (iOS Safari + tab-close). Uses
  // `fetch keepalive:true` internally (cannot use sendBeacon — beacons
  // can't carry the Authorization header).
  useEffect(() => {
    const handler = () => {
      useAudioStore.getState().fireUnloadBeacon();
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, []);

  if (hydrating) {
    return (
      <div className="min-h-screen bg-dream-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-dream-navy" aria-label="Wird geladen…" />
      </div>
    );
  }

  // Mini-Player visibility gate — per briefing K-03, hide on Story-Output
  // (`/story/:id`, inline player takes over) and on Now-Playing
  // (`/now-playing`, full player IS the page). Visible elsewhere when a
  // story is loaded AND has been played at least once (that check lives
  // inside MiniPlayer itself so it stays a pure presentational concern).
  //
  // Now-Playing also hides TopBar, BottomNav, EmailVerificationBanner,
  // and WorldSelectionSheet — the full-screen page owns its own chrome
  // (NowPlayingTopNav replaces TopBar). The singleton <audio> element
  // and the <main>/<Outlet> pair stay mounted so playback, the audio
  // store's _audio ref, and MediaSession handlers survive the route
  // change (a prior architecture nested NowPlaying OUTSIDE AppShell,
  // which unmounted the <audio> element mid-playback — Batch 6.2.3
  // post-smoke-test fix).
  const onStoryPage = /^\/story\/[^/]+$/.test(location.pathname);
  const onNowPlayingPage = location.pathname === '/now-playing';
  // PWA #16 Sub-Phase 16.6 — full-screen PDF viewer. Browser-mode never
  // navigates here (target="_blank" opens native viewer); the route is
  // PWA-only. The fixed-position iframe owns the viewport, so TopBar +
  // BottomNav + EmailVerificationBanner + MiniPlayer all suppress.
  const onPdfViewerPage = /^\/story\/[^/]+\/pdf$/.test(location.pathname);
  // The season view (/series/:id) hosts the full CombinedAudioPlayer, so hide
  // the MiniPlayer there (mirrors onStoryPage) — it surfaces only once the user
  // navigates away, while the combined track keeps playing.
  const onSeasonPage = /^\/series\/[^/]+$/.test(location.pathname);
  // The catalog reader is in-place state on /catalog/:slug (not a distinct
  // route), and hosts its own top-mounted player — suppress the MiniPlayer
  // while it's open to avoid a double player (`catalogReaderOpen` read above,
  // alongside the other hooks, so it stays before any early return).
  const renderMiniPlayer =
    !onStoryPage &&
    !onNowPlayingPage &&
    !onPdfViewerPage &&
    !onSeasonPage &&
    !catalogReaderOpen &&
    !catalogWorldCombinedPlayerOpen;
  // Sebastian ruling 2026-09-15 round 5 — /account/password + /account/
  // delete are the two /account/* routes reachable by a book-only
  // customer via OnboardingGuard's bypass set (round 3). AppShell's
  // audio chrome (worlds row + BottomNav + EmailVerificationBanner +
  // WorldSelectionSheet) leads that customer into audio onboarding —
  // a dead end for anyone with no subscription. Suppress the chrome
  // for `!hasAudioWorld` on those two paths only. Audio + both-worlds
  // customers see chrome unchanged; the guard's shared bypass set is
  // the single source of truth so this suppression cannot drift from
  // the guard whitelist.
  const isNoAudioAccountBypass =
    user !== null && !user.hasAudioWorld && ONBOARDING_BYPASS_PATHS.has(location.pathname);
  const renderChrome = !onNowPlayingPage && !onPdfViewerPage && !isNoAudioAccountBypass;

  const worldMatch = WORLD_ROUTE_PATTERN.exec(location.pathname);
  const outerBgClass =
    worldMatch && worldMatch[1] ? WORLD_ROUTE_BG[worldMatch[1]] : 'bg-dream-background';

  // Batch 10 Sub-phase D — wrap the entire AppShell tree in
  // WorldSelectionSheetProvider so descendant routes/components
  // (StartRoute Plan-Status-Card, StoryFooterActions, LibraryRoute
  // empty-state, AccountRoute Plan-Status-Card) can trigger the sheet
  // via `useWorldSelectionSheet().openSheet()`. State stays in this
  // component (sheetOpen + setSheetOpen above); the Context is purely
  // a callback transport so the existing renderChrome unmount-on-
  // /now-playing behavior for the sheet stays intact below.
  return (
    <WorldSelectionSheetProvider openSheet={() => setSheetOpen(true)}>
      <div className={`min-h-screen ${outerBgClass}`}>
        {renderChrome && <EmailVerificationBanner />}

        {renderChrome && <TopBar />}

        <main className={renderChrome ? 'pb-[calc(10rem+env(safe-area-inset-bottom,0))]' : ''}>
          <Outlet />
        </main>

        {/* Hidden singleton audio element — store holds a non-reactive ref.
          MUST stay mounted across all routes inside AppShell, including
          /now-playing — unmounting it tears down the <audio> DOM node
          and kills playback. */}
        <audio
          ref={audioRef}
          preload="metadata"
          onTimeUpdate={(e) => onTimeUpdate((e.target as HTMLAudioElement).currentTime)}
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onEnded}
          onError={() => onAudioError('playback-error')}
        />

        {renderMiniPlayer && <MiniPlayer />}

        {renderChrome && <BottomNav onPlusClick={() => setSheetOpen(true)} />}

        {renderChrome && <WorldSelectionSheet open={sheetOpen} onOpenChange={setSheetOpen} />}
      </div>
    </WorldSelectionSheetProvider>
  );
}
